import * as React from 'react';
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';
import { cn } from '../lib/utils.js';

// ─── Types ────────────────────────────────────────────────────

interface InfoHintProps {
  /** Hint content shown in the panel. Pass translated strings from the consuming app. */
  children: React.ReactNode;
  /** Accessible label for the icon trigger. */
  label?: string;
  /** Preferred side for the panel. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Alignment relative to the trigger. */
  align?: 'start' | 'center' | 'end';
  /** Icon size in pixels. */
  iconSize?: number;
  /** Extra classes for the icon trigger. */
  className?: string;
  /** Extra classes for the panel. */
  contentClassName?: string;
}

// ─── Component ───────────────────────────────────────────────

/**
 * Small "(i)" affordance that reveals a short help text.
 *
 * Hovering or keyboard-focusing opens it transiently; clicking pins it open so
 * it never vanishes on click (the trap that makes a plain hover-tooltip feel
 * broken). A pinned hint closes on outside-click, Escape, or blur.
 *
 * Built on the Popover primitive with a compact, readable card style suited to
 * multi-line copy. Self-contained — no provider wiring needed.
 */
function InfoHint({
  children,
  label = 'More information',
  side = 'top',
  align = 'center',
  iconSize = 14,
  className,
  contentClassName,
}: InfoHintProps) {
  const [open, setOpen] = React.useState(false);
  // A click pins the hint open so moving the pointer away does not dismiss it.
  const pinnedRef = React.useRef(false);

  function close() {
    pinnedRef.current = false;
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // Radix requests close on outside-click / Escape — honour it and unpin.
        if (!next) close();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => {
            if (!pinnedRef.current) setOpen(false);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            if (!pinnedRef.current) setOpen(false);
          }}
          onClick={(event) => {
            // Keep Radix from toggling a hover-open hint closed. Using the Trigger
            // still lets Radix identify clicks on the anchor as inside the popover.
            event.preventDefault();
            pinnedRef.current = true;
            setOpen(true);
          }}
          className={cn(
            'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground/70 transition-colors',
            'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          <Info size={iconSize} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        // Keep focus on the trigger — this is a passive hint, not an interactive surface.
        onOpenAutoFocus={(event) => event.preventDefault()}
        className={cn(
          'w-auto max-w-[260px] border border-border bg-popover px-3 py-2 text-left text-xs leading-relaxed text-popover-foreground shadow-md',
          contentClassName,
        )}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

export { InfoHint };
export type { InfoHintProps };
